import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { TableCell, TableRow } from '@mui/material';
import { get } from '@totalsoft/change-tracking-react';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { PARTNERS } from 'features/common/queries';
import { onSimpleSelectChange, valueOrDefault } from 'utils/functions';
import ActionsTableCell from 'components/common/table/ActionsTableCell';
import IconButton from 'components/common/buttons/IconButton';

const LeasingIncomeSchemaXPartnerComponent = ({ lens, validation, onDelete, onOpenMasterPartner, canReadMasterPartner, disabled }) => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const loadPartners = useCallback(
    async search => {
      if (!search) return [];
      const { data } = await apolloClient.query({
        query: PARTNERS,
        variables: { search }
      });
      return (data?.partners || []).map(partner => ({
        id: partner.partnerId,
        code: partner.code,
        name: partner.name,
        uniqueId: partner.uniqueId
      }));
    },
    [apolloClient]
  );

  const partner = useMemo(() => ({ id: lens.partnerId |> get, name: lens.partnerName |> get }), [lens.partnerId, lens.partnerName]);
  const partnerId = lens.partnerId |> get;

  return (
    <TableRow>
      <TableCell>
        <Autocomplete
          value={partner}
          loadOptions={loadPartners}
          onChange={onSimpleSelectChange(lens, 'partner')}
          error={!isValid(validation.partnerId)}
          helperText={getErrors(validation.partnerId)}
          disabled={disabled}
        />
      </TableCell>
      <TableCell>
        <TextField fullWidth value={lens.partnerCode |> get |> valueOrDefault('')} variant='standard' disabled />
      </TableCell>
      <TableCell>
        <TextField fullWidth value={lens.uniqueId |> get |> valueOrDefault('')} variant='standard' disabled />
      </TableCell>
      <ActionsTableCell width='100px'>
        <IconButton
          icon='person'
          color='primary'
          size='tiny'
          variant='text'
          tooltip={t('LeasingIncomeSchema.OpenPartner')}
          onClick={onOpenMasterPartner(partnerId)}
          disabled={!canReadMasterPartner || !partnerId}
        />
        <IconButton
          type='delete'
          color='primary'
          size='tiny'
          variant='text'
          tooltip={t('Delete')}
          onClick={() => onDelete(lens.key |> get)}
          disabled={disabled}
        />
      </ActionsTableCell>
    </TableRow>
  );
};

LeasingIncomeSchemaXPartnerComponent.propTypes = {
  lens: PropTypes.object,
  validation: PropTypes.object,
  onDelete: PropTypes.func,
  onOpenMasterPartner: PropTypes.func.isRequired,
  canReadMasterPartner: PropTypes.bool,
  disabled: PropTypes.bool
};

export default LeasingIncomeSchemaXPartnerComponent;
